/**
 * IconKey — legend of every marker that can appear on a listing tile.
 *
 * Grouped into two sections:
 *   1. Card Type chips (the coloured badge strip at the TOP of each tile)
 *   2. Seller / Deal signals (the small chips + top-hat in the BOTTOM-RIGHT
 *      corner of each tile)
 *
 * Rendered in the desktop Browse sidebar and in the mobile filter drawer so
 * buyers can immediately decode any tile without hover-hunting for tooltips.
 * All chip classes here mirror ListingCard.jsx so what buyers see in the key
 * is exactly what they see on the tile.
 */
import { Package, CreditCard, ArrowsClockwise } from "@phosphor-icons/react";
import { FoundingHat } from "@/components/FoundingHat";

const CARD_TYPE_ITEMS = [
  { chip: "Base",     cls: "",                    desc: "Base card" },
  { chip: "Insert",   cls: "h57-chip-blue",       desc: "Insert set" },
  { chip: "Var",      cls: "h57-chip-blue",       desc: "Variation" },
  { chip: "RC",       cls: "h57-chip-orange",     desc: "Rookie card" },
  { chip: "Auto",     cls: "h57-chip-gold",       desc: "Autograph" },
  { chip: "Relic",    cls: "",                    desc: "Relic / Player-worn / Game-used" },
  { chip: "/249",     cls: "h57-chip-orange",     desc: "Numbered (serial number)" },
  { chip: "SSP",      cls: "h57-chip-gold",       desc: "Super Short Print" },
  { chip: "SP",       cls: "h57-chip-gold",       desc: "Short Print" },
  { chip: "PSA 10",   cls: "h57-chip-blue",       desc: "Graded (any company)" },
];

export default function IconKey({ testId = "icon-key" }) {
  return (
    <div className="pt-4 border-t border-white/10" data-testid={testId}>
      <div className="h57-label mb-2">Icon Key</div>

      {/* Card-type chips (top badge strip) */}
      <div className="mb-3">
        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1.5">Card type (top of tile)</div>
        <ul className="space-y-1.5">
          {CARD_TYPE_ITEMS.map((it) => (
            <li key={it.chip} className="flex items-center gap-2 text-[11px] text-zinc-400">
              <span className={`h57-chip shrink-0 ${it.cls}`}>{it.chip}</span>
              <span className="truncate">{it.desc}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Seller & deal signals (bottom-right of tile) */}
      <div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1.5">Seller & deal signals</div>
        <ul className="space-y-1.5">
          <li className="flex items-center gap-2 text-[11px] text-zinc-400">
            <span className="shrink-0"><FoundingHat founding size={13} /></span>
            <span>Founding Member</span>
          </li>
          <li className="flex items-center gap-2 text-[11px] text-zinc-400">
            <span
              className="inline-flex items-center gap-0.5 border border-[#007AFF] text-[#007AFF] px-1 py-0.5 uppercase tracking-widest text-[9px] font-bold leading-none whitespace-nowrap shrink-0"
            >
              <Package size={9} weight="bold" />
              Combo
            </span>
            <span>Combined shipping offered</span>
          </li>
          <li className="flex items-center gap-2 text-[11px] text-zinc-400">
            <span
              className="inline-flex items-center gap-0.5 border border-[#FF5F1F] text-[#FF5F1F] px-1 py-0.5 uppercase tracking-widest text-[9px] font-bold leading-none whitespace-nowrap shrink-0"
            >
              <CreditCard size={9} weight="bold" />
              CC
            </span>
            <span>Seller accepts credit card</span>
          </li>
          <li className="flex items-center gap-2 text-[11px] text-zinc-400">
            <span
              className="inline-flex items-center gap-0.5 border border-emerald-500 text-emerald-400 px-1 py-0.5 uppercase tracking-widest text-[9px] font-bold leading-none whitespace-nowrap shrink-0"
            >
              <ArrowsClockwise size={9} weight="bold" />
              Offers
            </span>
            <span>Seller accepts offers</span>
          </li>
          <li className="flex items-center gap-2 text-[11px] text-zinc-400">
            <span
              className="inline-flex items-center border border-red-500 text-red-400 px-1 py-0.5 uppercase tracking-widest text-[9px] font-bold leading-none whitespace-nowrap shrink-0"
            >
              Firm
            </span>
            <span>Firm price · no offers</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
